import { useLazyGetAuctionForms } from "apollo/hooks/queries";
import { useEnvContext } from "context/EnvContext";
import {
  isRehabFormType,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
} from "core/consts";
import { activateEligibilityFiles } from "core/model/utils/featureFlags";
import { AllPatientForms, QueryProgress } from "core/types";
import { useEffect, useState } from "react";
import { convertAuctionFormsToRehabForms } from "../PatientForms/RehabForms/utils";

export function useGetFormFiles({
  auctionId,
  isEligiblePage,
  patientId,
}: {
  auctionId: number;
  isEligiblePage?: boolean;
  patientId: number;
}) {
  const { isReceiverApp } = useEnvContext();

  const [formsProgress, setFormsProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [allForms, setAllForms] = useState<AllPatientForms | undefined>(
    undefined,
  );

  const getAuctionForms = useLazyGetAuctionForms();

  useEffect(() => {
    const shouldLoadForms =
      activateEligibilityFiles && !isEligiblePage && !isReceiverApp;

    if (!shouldLoadForms) {
      setFormsProgress(QUERY_PROGRESS_NOT_STARTED);
      setAllForms(undefined);
      return;
    }

    const loadForms = async () => {
      setFormsProgress(QUERY_PROGRESS_PENDING);

      try {
        const auctionForms = await getAuctionForms({ auctionId });
        const rehabForms = convertAuctionFormsToRehabForms(
          auctionForms?.filter((f) => isRehabFormType(f.form_type)),
        );
        const transitionalCareForm = auctionForms?.find(
          (f) => f.form_type === RECARE_FILE_TYPE.TRANSITIONAL_CARE,
        );
        setAllForms({
          ...rehabForms,
          transitional_care_form: transitionalCareForm,
        });
        setFormsProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        console.error("Error loading forms:", error);
        setFormsProgress(QUERY_PROGRESS_NOT_STARTED);
      }
    };

    loadForms();
  }, [
    activateEligibilityFiles,
    isEligiblePage,
    isReceiverApp,
    auctionId,
    patientId,
  ]);

  return {
    allForms,
    getFormsProgress: formsProgress,
  };
}
